import './ServicePropertiesForm.scss';

import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Form, Tab, Tabs } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { updateProperties } from '../../slices/inspectorSlice';
import { BdaServiceProperty } from '../../models/BdaService';
import { Avatar, Select } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { RootState } from '../../store';
import ServicePropertyFormGroup from './ServicePropertyFormGroup';

type Props = {
  properties: BdaServiceProperty[];
  agentMode?: boolean;
  onAgentFieldChange?: (field: string, value: string) => void;
  onPropertyModalOpen?: (prop: BdaServiceProperty) => () => void;
  isFromPage?: boolean;
  handleUpdateProperties?: (properties: BdaServiceProperty[]) => void;
}

export default function ServicePropertiesForm({ properties, agentMode, onAgentFieldChange, onPropertyModalOpen, isFromPage, handleUpdateProperties }: Props) {
  const dispatch = useDispatch();
  const [editingProperties, setEditingProperties] = useState(properties);
  const environmentVariables = useSelector((state: RootState) => state.designer.environmentVariables);
  const variablesOptions = useMemo(() => environmentVariables?.map(env => ({ label: env.name, value: env.id, image: env.img })), [environmentVariables]);

  useEffect(() => {
    setEditingProperties(properties);
  }, [properties]);

  const updateServiceProperties = useCallback((_evt?: any, newProperties?: BdaServiceProperty[]) => {
    if (isFromPage) handleUpdateProperties?.(newProperties || editingProperties);
    else dispatch(updateProperties(newProperties || editingProperties));
  }, [dispatch, editingProperties, isFromPage, handleUpdateProperties]);

  const handlePropChange = useCallback((key: string, checkbox?: boolean, pure?: boolean) => (evt: any) => {
    setEditingProperties(curr => {
      const value = pure ? evt : checkbox ? evt.target.checked : evt.target.value;
      const index = curr.findIndex(prop => prop.key === key);
      const newProperties = cloneDeep(curr);
      newProperties[index].value = value;
      if (checkbox) updateServiceProperties(null, newProperties);
      return newProperties;
    });
  }, [updateServiceProperties]);

  const handleBlur = useCallback((prop: BdaServiceProperty) => () => {
    if (agentMode && [window.__env.DESIGNER__AGENT_LLM_MODEL_PROPERTY, window.__env.DESIGNER__AGENT_PROMPT_PROPERTY].includes(prop.key)) onAgentFieldChange?.(prop.key, prop.value || "");
    updateServiceProperties();
  }, [agentMode, onAgentFieldChange, updateServiceProperties]);

  const toggleInterpolation = useCallback((id: number) => () => {
    setEditingProperties(curr => {
      const index = curr.findIndex(prop => prop.id === id);
      const newProperties = cloneDeep(curr);
      newProperties[index].interpolationEnabled = !newProperties[index].interpolationEnabled;
      updateServiceProperties(null, newProperties);
      return newProperties;
    });
  }, [updateServiceProperties]);

  const tabs = useMemo(() => {
    return editingProperties
      .filter(prop => !prop.invisible)
      .reduce((acc, curr) => {
        const type = curr.type;
        if (acc[type]) acc[type].push(curr);
        else acc[type] = [curr];
        return acc;
      }, {} as Record<string, BdaServiceProperty[]>);
  }, [editingProperties]);

  const defaultTab = useMemo(() => {
    return tabs['application']?.length ? 'application' : 'static';
  }, [tabs]);

  const renderAgentLlmModelConfig = useCallback((prop: BdaServiceProperty) => {
    return (
      <Form.Group controlId={`${prop.key}-form-group`} className="mb-3" key={prop.key}>
        <Form.Label>{prop.key}</Form.Label>
        <Select
          className="w-100 h-auto"
          value={prop.value}
          onChange={handlePropChange(prop.key!, false, true)}
          onBlur={handleBlur(prop)}
          placeholder={`${prop.key} value`}
          allowClear
        >
          {variablesOptions?.map(option => (
            <Select.Option key={option.value} label={option.label} value={option.value}>
              <Avatar
                src={option.image}
                icon={<FontAwesomeIcon icon={solid('image')} size="sm" />}
                className="border me-2"
                style={{ height: 25, width: 25 }}
              />
              <span>{option.label}</span>
            </Select.Option>
          ))}
        </Select>
      </Form.Group>
    )
  }, [handleBlur, handlePropChange, variablesOptions]);

  return (
    <div className="service-properties-form">
      <Tabs defaultActiveKey={defaultTab}>
        {Object.entries(tabs)
          .sort()
          .map(([tab, properties]) => (
            <Tab key={tab} title={tab} eventKey={tab}>
              <div className="tab-content-wrapper">
                {properties
                  .sort((a, b) => {
                    if (a.mandatory && !b.mandatory) return -1;
                    else if (b.mandatory) return 0;
                    else return 1;
                  })
                  .map(prop => prop.key === window.__env.DESIGNER__AGENT_LLM_MODEL_PROPERTY
                    ? renderAgentLlmModelConfig(prop)
                    : (
                      <ServicePropertyFormGroup
                        key={prop.key}
                        prop={prop}
                        onPropChange={handlePropChange}
                        onBlur={handleBlur(prop)}
                        updateServiceProperties={updateServiceProperties}
                        onOpenModal={onPropertyModalOpen?.(prop)}
                        onToggleInterpolation={toggleInterpolation(prop.id!)}
                        className="mb-3"
                      />
                    ))}
              </div>
            </Tab>
          ))}
      </Tabs>
    </div>
  )
}
